// src/pages/contentManagement/podcasts/podcastList.js
import React, { useCallback, useEffect, useMemo, useReducer, useState } from "react";
import { Button, Collapse, Divider, Form, Image, Input, Modal, Pagination, Table, Tooltip, Upload } from "antd";
import { FaTrash, FaUpload, FaChevronDown, FaImage, FaInfoCircle } from "react-icons/fa";
import axios from "../../../services/axiosConfig";
import API_ENDPOINTS from "../../../services/apiEndpoints";
import { getAuthToken } from "../../../auth/auth";
import useNotification from "../../../hooks/useNotification";
import Layout from "../../../components/layout/layout";
import TableLayout from "../../../components/tableLayout/tableLayout";
import TopToolBar from "../../../components/topToolBar/topToolBar";
import "./podcastList.css";

// Initial state for useReducer
const initialState = {
  isLoading: true,
  searchTerm: "",
  filters: {},
  podcasts: [],
  currentPage: 1,
  pageSize: 10,
  sortConfig: { key: "uploadedAt", direction: "desc" },
  showDeleteModal: false,
  deletePodcastId: null
};

// Reducer for state management
const podcastReducer = (state, action) => {
  switch (action.type) {
    case "setLoading":
      return { ...state, isLoading: action.payload };
    case "setSearchTerm":
      return { ...state, searchTerm: action.payload, currentPage: 1 };
    case "setFilters":
      return { ...state, filters: action.payload, currentPage: 1 };
    case "setPodcasts":
      return { ...state, podcasts: action.payload };
    case "setPagination":
      return {
        ...state,
        currentPage: action.payload.page,
        pageSize: action.payload.pageSize
      };
    case "setSortConfig":
      return { ...state, sortConfig: action.payload };
    case "setDeleteModal":
      return {
        ...state,
        showDeleteModal: action.payload.show,
        deletePodcastId: action.payload.podcastId || null
      };
    case "removePodcast":
      return {
        ...state,
        podcasts: state.podcasts.filter((podcast) => podcast.id !== action.payload)
      };
    case "addPodcast":
      return {
        ...state,
        podcasts: [action.payload, ...state.podcasts]
      };
    case "resetFilters":
      return {
        ...state,
        filters: {},
        searchTerm: "",
        currentPage: 1
      };
    default:
      return state;
  }
};

function PodcastManagement() {
  const { notification, showNotification, hideNotification } = useNotification();
  const [state, dispatch] = useReducer(podcastReducer, initialState);
  const [form] = Form.useForm();

  // Additional state for upload and notifications
  const [uploadLoading, setUploadLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [thumbnail, setThumbnail] = useState(null);
  const [duration, setDuration] = useState("");
  const [speaker, setSpeaker] = useState("");

  const { isLoading, searchTerm, filters, podcasts, currentPage, pageSize, sortConfig, showDeleteModal, deletePodcastId } = state;

  // Configuration objects
  const filtersConfig = useMemo(
    () => ({
      uploadDate: {
        label: "Upload Date",
        options: [
          { value: "newest", label: "Newest" },
          { value: "last7Days", label: "Last 7 Days" },
          { value: "last30Days", label: "Last 30 Days" },
          { value: "oldest", label: "Oldest" }
        ]
      },
      durationType: {
        label: "Duration",
        options: [
          { value: "short", label: "Short (< 30 min)" },
          { value: "medium", label: "Medium (30-60 min)" },
          { value: "long", label: "Long (> 60 min)" }
        ]
      }
    }),
    []
  );

  // Date filtering utility
  const filterByDate = useCallback((items, dateFilter) => {
    if (!dateFilter) return items;

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    switch (dateFilter) {
      case "newest":
        return [...items].sort((a, b) => new Date(b.uploadedAt) - new Date(a.uploadedAt));
      case "oldest":
        return [...items].sort((a, b) => new Date(a.uploadedAt) - new Date(b.uploadedAt));
      case "last7Days": {
        const sevenDaysAgo = new Date(today);
        sevenDaysAgo.setDate(today.getDate() - 7);
        return items.filter((item) => new Date(item.uploadedAt) >= sevenDaysAgo);
      }
      case "last30Days": {
        const thirtyDaysAgo = new Date(today);
        thirtyDaysAgo.setDate(today.getDate() - 30);
        return items.filter((item) => new Date(item.uploadedAt) >= thirtyDaysAgo);
      }
      default:
        return items;
    }
  }, []);

  // Global search function
  const globalSearch = useCallback((items, term) => {
    if (!term?.trim()) return items;

    const searchLower = term.toLowerCase().trim();

    return items.filter((item) => {
      const searchFields = [
        item.fileName || "",
        item.speaker || "",
        item.duration?.toString() || "",
        item.uploadedAt
          ? new Date(item.uploadedAt)
              .toLocaleDateString("en-GB", {
                timeZone: "Asia/Kolkata",
                day: "2-digit",
                month: "short",
                year: "numeric"
              })
              .replace(/ /g, "-")
          : ""
      ];

      return searchFields.some((field) => field.toLowerCase().includes(searchLower));
    });
  }, []);

  // Apply all filters and search
  const filteredData = useMemo(() => {
    let result = [...podcasts];

    // Apply date filter first (includes sorting)
    result = filterByDate(result, filters.uploadDate);

    // Apply duration type filter
    if (filters.durationType) {
      switch (filters.durationType) {
        case "short":
          result = result.filter((podcast) => parseFloat(podcast.duration) < 30);
          break;
        case "medium":
          result = result.filter((podcast) => {
            const dur = parseFloat(podcast.duration);
            return dur >= 30 && dur <= 60;
          });
          break;
        case "long":
          result = result.filter((podcast) => parseFloat(podcast.duration) > 60);
          break;
        default:
          break;
      }
    }

    // Apply global search
    result = globalSearch(result, searchTerm);

    // Apply sorting if no date filter is applied
    if (!filters.uploadDate && sortConfig) {
      result.sort((a, b) => {
        const { key, direction } = sortConfig;
        if (key === "uploadedAt") {
          return direction === "asc" ? new Date(a.uploadedAt) - new Date(b.uploadedAt) : new Date(b.uploadedAt) - new Date(a.uploadedAt);
        }
        if (key === "duration") {
          return direction === "asc" ? parseFloat(a.duration || 0) - parseFloat(b.duration || 0) : parseFloat(b.duration || 0) - parseFloat(a.duration || 0);
        }
        const valA = a[key]?.toString().toLowerCase() || "";
        const valB = b[key]?.toString().toLowerCase() || "";
        if (valA < valB) return direction === "asc" ? -1 : 1;
        if (valA > valB) return direction === "asc" ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [podcasts, filters, searchTerm, filterByDate, globalSearch, sortConfig]);

  // Get current page data
  const currentPageData = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredData.slice(startIndex, endIndex);
  }, [filteredData, currentPage, pageSize]);

  // Fetch Podcasts
  const fetchPodcasts = useCallback(async () => {
    dispatch({ type: "setLoading", payload: true });
    try {
      const token = await getAuthToken();
      const response = await axios.get(API_ENDPOINTS.PODCASTS, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.status === 200 && Array.isArray(response.data)) {
        dispatch({ type: "setPodcasts", payload: response.data });
      } else {
        throw new Error("Unexpected API response format.");
      }
    } catch (error) {
      console.error("Error fetching podcasts:", error);
      showNotification("Failed to load podcasts", "error");
    } finally {
      dispatch({ type: "setLoading", payload: false });
    }
  }, [showNotification]);

  // Handle File Selection
  const handleFileChange = useCallback(
    (file) => {
      if (file.type !== "audio/mpeg") {
        showNotification("Only MP3 files are allowed. Please select a valid MP3 file.", "error");
        return false;
      }

      setSelectedFile(file);

      const audio = new Audio(URL.createObjectURL(file));

      audio.addEventListener("loadedmetadata", () => {
        if (audio.duration) {
          const calculatedDuration = (audio.duration / 60).toFixed(2);
          setDuration(calculatedDuration);
          form.setFieldsValue({ duration: calculatedDuration });
        } else {
          setDuration("");
          form.setFieldsValue({ duration: "" });
          showNotification("Could not read audio duration. Please try another file.", "error");
        }
      });

      return false; // Prevent auto upload
    },
    [showNotification, form]
  );

  // Handle Thumbnail Selection
  const handleThumbnailChange = useCallback(
    (file) => {
      if (!file.type.startsWith("image/")) {
        showNotification("Only image files are allowed for thumbnail.", "error");
        return false;
      }

      setThumbnail(file);
      return false; // Prevent auto upload
    },
    [showNotification]
  );

  // Handle Upload
  const handleUpload = useCallback(async () => {
    if (!selectedFile || !thumbnail || !speaker.trim()) {
      showNotification("Please fill in all fields before submitting.", "error");
      return;
    }

    setUploadLoading(true);

    try {
      const token = await getAuthToken();

      // Step 1: Get signed URLs
      const requestBody = {
        fileName: selectedFile.name,
        fileType: selectedFile.type,
        thumbnailName: thumbnail.name,
        thumbnailType: thumbnail.type,
        duration: duration,
        speaker: speaker
      };

      const response = await axios.post(`${API_ENDPOINTS.PODCASTS}/generateSignedUrls`, requestBody, { headers: { Authorization: `Bearer ${token}` } });

      const { audioUploadUrl, thumbnailUploadUrl, audioFileKey, thumbnailFileKey } = response.data;

      if (!audioFileKey || !thumbnailFileKey) {
        throw new Error("Missing file keys in the response.");
      }

      // Step 2: Upload files to S3
      const uploadToS3 = async (uploadUrl, file) => {
        await axios.put(uploadUrl, file, {
          headers: { "Content-Type": file.type }
        });
      };

      await uploadToS3(audioUploadUrl, selectedFile);
      await uploadToS3(thumbnailUploadUrl, thumbnail);

      // Step 3: Save metadata
      await axios.post(
        `${API_ENDPOINTS.PODCASTS}/metadata`,
        {
          fileName: selectedFile.name,
          audioFileKey,
          thumbnailFileKey,
          duration: duration,
          speaker: speaker
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      showNotification("Podcast uploaded successfully!", "success");
      setSelectedFile(null);
      setThumbnail(null);
      setDuration("");
      setSpeaker("");
      form.resetFields();
      await fetchPodcasts();
    } catch (error) {
      console.error("Upload failed:", error.response?.data || error.message);
      showNotification(error.response?.data?.message || "Upload failed. Please try again.", "error");
    } finally {
      setUploadLoading(false);
    }
  }, [selectedFile, thumbnail, duration, speaker, fetchPodcasts, showNotification, form]);

  // Handle Cancel - Clear form
  const handleCancel = useCallback(() => {
    setSelectedFile(null);
    setThumbnail(null);
    setDuration("");
    setSpeaker("");
    form.resetFields();
    showNotification("Form cleared.", "info");
  }, [form, showNotification]);

  // Handle Delete
  const confirmDelete = useCallback((id) => {
    dispatch({
      type: "setDeleteModal",
      payload: { show: true, podcastId: id }
    });
  }, []);

  const handleDelete = useCallback(async () => {
    if (!deletePodcastId) return;

    dispatch({ type: "setDeleteModal", payload: { show: false } });
    dispatch({ type: "setLoading", payload: true });

    try {
      const token = await getAuthToken();
      const response = await axios.delete(API_ENDPOINTS.PODCASTS, {
        data: { podcastId: deletePodcastId },
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.status === 200 || response.status === 204) {
        dispatch({ type: "removePodcast", payload: deletePodcastId });
        showNotification("Podcast deleted successfully.", "success");
      } else {
        throw new Error("Failed to delete the podcast.");
      }
    } catch (error) {
      console.error("Error deleting the podcast:", error);
      showNotification("Failed to delete the podcast.", "error");
    } finally {
      dispatch({ type: "setLoading", payload: false });
    }
  }, [deletePodcastId, showNotification]);

  const handleDeleteModalCancel = useCallback(() => {
    dispatch({ type: "setDeleteModal", payload: { show: false } });
  }, []);

  // Event handlers
  const handleSearch = useCallback((term) => {
    dispatch({ type: "setSearchTerm", payload: term });
  }, []);

  const handleFilterChange = useCallback((newFilters) => {
    dispatch({ type: "setFilters", payload: newFilters });
  }, []);

  const handleReset = useCallback(() => {
    dispatch({ type: "resetFilters" });
  }, []);

  const handlePaginationChange = useCallback((page, size) => {
    dispatch({ type: "setPagination", payload: { page, pageSize: size } });
  }, []);

  // Table columns configuration
  const columns = useMemo(
    () => [
      {
        title: "SL. NO.",
        render: (_, __, index) => (currentPage - 1) * pageSize + index + 1,
        width: 80
      },
      {
        title: "PODCAST NAME",
        dataIndex: "fileName",
        sorter: (a, b) => (a.fileName || "").localeCompare(b.fileName || ""),
        render: (fileName) => fileName || "-"
      },
      {
        title: "UPLOADED DATE",
        dataIndex: "uploadedAt",
        sorter: (a, b) => new Date(a.uploadedAt) - new Date(b.uploadedAt),
        render: (date) =>
          date
            ? new Date(date)
                .toLocaleDateString("en-GB", {
                  timeZone: "Asia/Kolkata",
                  day: "2-digit",
                  month: "short",
                  year: "numeric"
                })
                .replace(/ /g, "-")
            : "-"
      },
      {
        title: "DURATION",
        dataIndex: "duration",
        sorter: (a, b) => parseFloat(a.duration || 0) - parseFloat(b.duration || 0),
        render: (duration) => (duration ? `${duration} min` : "-"),
        width: 120
      },
      {
        title: "SPEAKER",
        dataIndex: "speaker",
        sorter: (a, b) => (a.speaker || "").localeCompare(b.speaker || ""),
        render: (speaker) => speaker || "-"
      },
      {
        title: "THUMBNAIL",
        dataIndex: "thumbnailUrl",
        render: (thumbnailUrl) => (thumbnailUrl ? <Image src={thumbnailUrl} alt="Thumbnail" width={50} height={50} style={{ objectFit: "cover", borderRadius: "4px" }} /> : "-"),
        width: 100
      },
      {
        title: "ACTIONS",
        render: (_, record) => (
          <div className="podm-list-btn-group" onClick={(e) => e.stopPropagation()}>
            <Tooltip title="Delete">
              <button className="podm-list-delete-btn" onClick={() => confirmDelete(record.id)}>
                <FaTrash />
              </button>
            </Tooltip>
          </div>
        ),
        width: 120
      }
    ],
    [currentPage, pageSize, confirmDelete]
  );

  // Excel export data
  const prepareExcelData = useCallback(() => {
    return filteredData.map((podcast, index) => ({
      "SL. NO.": index + 1,
      "Podcast Name": podcast.fileName,
      "Uploaded Date": podcast.uploadedAt
        ? new Date(podcast.uploadedAt)
            .toLocaleDateString("en-GB", {
              timeZone: "Asia/Kolkata",
              day: "2-digit",
              month: "short",
              year: "numeric"
            })
            .replace(/ /g, "-")
        : "-",
      Duration: podcast.duration ? `${podcast.duration} min` : "-",
      Speaker: podcast.speaker || "-"
    }));
  }, [filteredData]);

  // Effects
  useEffect(() => {
    fetchPodcasts();
  }, [fetchPodcasts]);

  return (
    <Layout
      navTitle="Content Management"
      pageTitle="Podcasts"
      notificationProps={[
        {
          show: notification.show,
          message: notification.message,
          type: notification.type,
          onClose: hideNotification,
          duration: 4000
        }
      ]}
    >
      <div className="podm-upload-section">
        <Collapse
          expandIcon={({ isActive }) => (
            <FaChevronDown
              style={{
                transform: isActive ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.3s"
              }}
            />
          )}
          className="podm-upload-collapse"
          items={[
            {
              key: "1",
              label: "Upload New Podcast",
              children: (
                <Form form={form} layout="vertical" className="podm-upload-form">
                  <Form.Item
                    label="Upload Podcast File"
                    name="podcastFile"
                    tooltip={{
                      title: "Accepted formats: MP3",
                      icon: <FaInfoCircle style={{ cursor: "pointer" }} />
                    }}
                  >
                    <Upload
                      className="podm-upload-input"
                      accept="audio/mpeg"
                      beforeUpload={handleFileChange}
                      fileList={selectedFile ? [selectedFile] : []}
                      onRemove={() => {
                        setSelectedFile(null);
                        setDuration("");
                        form.setFieldsValue({
                          podcastFile: undefined,
                          duration: ""
                        });
                      }}
                      maxCount={1}
                    >
                      <Button icon={<FaUpload />}>Select Podcast File</Button>
                    </Upload>
                  </Form.Item>

                  <Form.Item label="Upload Thumbnail" name="thumbnail">
                    <Upload
                      className="podm-upload-input"
                      accept="image/*"
                      beforeUpload={handleThumbnailChange}
                      fileList={thumbnail ? [thumbnail] : []}
                      onRemove={() => {
                        setThumbnail(null);
                        form.setFieldsValue({ thumbnail: undefined });
                      }}
                      maxCount={1}
                    >
                      <Button icon={<FaImage />}>Select Thumbnail</Button>
                    </Upload>
                  </Form.Item>

                  <Form.Item label="Duration (in minutes)" name="duration">
                    <Input className="podm-upload-input" value={duration} readOnly placeholder="Auto-detected" style={{ width: "200px" }} />
                  </Form.Item>

                  <Form.Item
                    label="Name of Speaker"
                    name="speaker"
                    rules={[
                      {
                        required: true,
                        message: "Please enter speaker name"
                      }
                    ]}
                  >
                    <Input className="podm-upload-input" value={speaker} onChange={(e) => setSpeaker(e.target.value)} placeholder="Enter speaker name" style={{ width: "300px" }} />
                  </Form.Item>

                  <Form.Item>
                    <div className="podm-form-btn-group">
                      <Button className="podm-form-cancel-btn" onClick={handleCancel} disabled={uploadLoading}>
                        Clear
                      </Button>
                      <Button
                        className="podm-form-submit-btn"
                        onClick={handleUpload}
                        loading={uploadLoading}
                        disabled={!selectedFile || !thumbnail || !speaker.trim() || uploadLoading}
                      >
                        {uploadLoading ? "Uploading..." : "Upload"}
                      </Button>
                    </div>
                  </Form.Item>
                </Form>
              )
            }
          ]}
        />
      </div>

      <Divider />

      <TableLayout
        toolbar={
          <TopToolBar
            filtersConfig={filtersConfig}
            searchPlaceholder="Search podcasts..."
            searchTerm={searchTerm}
            filters={filters}
            onSearch={handleSearch}
            onFilterChange={handleFilterChange}
            onReset={handleReset}
            showExcelDownload={true}
            excelData={prepareExcelData()}
            excelFilename="podcasts"
            excelSheetName="Podcasts"
          />
        }
      >
        <Table
          className="tpl-table"
          rowKey={(record) => record.id}
          rowClassName={(record, index) => (index % 2 === 0 ? "table-row-light" : "table-row-dark")}
          columns={columns}
          dataSource={currentPageData}
          loading={isLoading}
          pagination={false}
          expandable={{
            showExpandColumn: false
          }}
        />
        <div className="tpl-pagination-wrapper">
          <Pagination className="tpl-pagination" current={currentPage} pageSize={pageSize} total={filteredData.length} onChange={handlePaginationChange} showSizeChanger={false} />
        </div>
      </TableLayout>

      {/* Delete Confirmation Modal */}
      <Modal
        title="Confirm Deletion"
        open={showDeleteModal}
        onCancel={handleDeleteModalCancel}
        footer={[
          <div className="podm-modal-btn-group" key="modal-buttons">
            <Button className="podm-modal-cancel-btn" key="cancel" onClick={handleDeleteModalCancel}>
              Cancel
            </Button>
            <Button className="podm-modal-delete-btn" key="delete" onClick={handleDelete}>
              Delete
            </Button>
          </div>
        ]}
        width={400}
        centered
      >
        <p>Are you sure you want to delete this podcast?</p>
      </Modal>
    </Layout>
  );
}

export default PodcastManagement;
